import { Injectable, computed, inject, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  Country,
  ExcursionSearchCity,
  ExcursionSearchType,
  SearchOnlyExcursionPackagesRequest
} from '@appTypes/api.types';
import { deepClean } from '@core/misc/misc.utils';
import { minDate } from '@core/misc/validators.utils';
import { SVGIcon, SVG_ICONS } from '@data/svg-icons.data';
import { UpdateFormValue } from '@ngxs/form-plugin';
import { Store, createDispatchMap, createSelectMap } from '@ngxs/store';
import { map, of, switchMap } from 'rxjs';
import { DynamicDateClassLoadFn } from '@shared/components/dates/dynamic-date-picker.abstract';
import {
  excursionCalendarDateClassItems,
  excursionCalendarKey,
  excursionCalendarParams,
  localDateOf
} from '@pages/excursions-page/excursions-calendar.utils';
import { CHILDRAN_AGES } from '@pages/_common/static-data.const';
import { NGXS_EXCURSIONS_SEARCH_FORM } from '@pages/excursions-page/excursions-page.const';
import {
  excursionsGetDestinationCountries,
  excursionsGetSearchOptions,
  excursionsLoadCalendar,
  excursionsResetSearchResult,
  excursionsSearchPackages,
  excursionsSetKindsFilter
} from '@pages/excursions-page/state/excursions.actions';
import { ExcursionsState } from '@pages/excursions-page/state/excursions.state';
import { AppPersistedState } from '@app-state/app.persisted.state';
import { appSetDestinationCountry } from '@app-state/app.actions';

/** TravelDesk Plan 2b: the Excursions search form - destination, day, party, kinds; the search is live (D-21). */
@Injectable()
export class ExcursionsSearchFormService {
  readonly today = new Date(new Date().setHours(0, 0, 0, 0));
  readonly ngxsForm = NGXS_EXCURSIONS_SEARCH_FORM;
  readonly childrenAgesData = CHILDRAN_AGES;

  readonly #selectors = createSelectMap({
    destinationCountries: ExcursionsState.destinationCountries,
    destinationDefaultCountry: ExcursionsState.destinationDefaultCountry,
    searchOptions: ExcursionsState.searchOptions,
    searchFormData: ExcursionsState.searchFormData,
    formStatus: ExcursionsState.searchLoadStatus,
    kindsFilter: ExcursionsState.kindsFilter,
    activedDestinationCountry: AppPersistedState.activedDestinationCountry,
    activedCurrency: AppPersistedState.activedCurrency
  });

  readonly actions = createDispatchMap({
    updateFormValue: UpdateFormValue,
    getDestinationCountries: excursionsGetDestinationCountries,
    getSearchOptions: excursionsGetSearchOptions,
    resetSearchResult: excursionsResetSearchResult,
    searchPackages: excursionsSearchPackages,
    setKindsFilter: excursionsSetKindsFilter,
    loadCalendar: excursionsLoadCalendar,
    rememberCountry: appSetDestinationCountry
  });
  readonly #store = inject(Store);

  /**
   * 27.09.2026 (Amiran: "as a user I want to identify in the calendar on which day an excursion is available"): the date picker's day marks,
   * one read per visible month for the form's destination, cities, categories and party - a green day with the number of excursions on sale
   * under it (mockup option B). No destination yet = no marks and no call.
   */
  readonly calendarDateClassFn$: DynamicDateClassLoadFn = ({ month, year }) => {
    const v = this.formGroup.getRawValue();
    if (!v.countryCode) return of([]);
    const params = excursionCalendarParams(
      {
        countryCode: v.countryCode,
        cityUIDs: v.cityUIDs ?? [],
        excursionTypeUIDs: v.excursionTypeUIDs ?? [],
        adults: v.adults ?? 1,
        children: v.childrenAges.length
      },
      year,
      month
    );
    const identifier = excursionCalendarKey(params);
    const calendar$ = this.#store.select(ExcursionsState.calendarByParams(identifier));
    return this.actions
      .loadCalendar(params, identifier)
      .pipe(switchMap(() => calendar$))
      .pipe(map((c) => excursionCalendarDateClassItems(c?.response)));
  };

  /** A day offered by the empty-result message ('yyyy-MM-dd'): put it in the form and search it. */
  pickDate(isoDate: string) {
    const date = localDateOf(isoDate);
    if (!date) return;
    this.formGroup.controls.date.setValue(date);
    this.onSubmit();
  }

  readonly isSubmited = signal<boolean>(false);
  readonly countriesTypedToken!: Country;
  /** 25.09.2026 (Amiran: "why no flags you not follow our website pattern"): the country's flag, exactly as the Tours / Hotels forms draw it */
  readonly countriesIconGetter = (c?: Country) => {
    if (c?.countryCode === null) return 'map-marker';
    const icon = c?.countryCode ? (`flag-${c.countryCode.toLowerCase()}` as SVGIcon) : 'map-marker';
    return SVG_ICONS?.[icon] ? icon : 'map-marker';
  };
  readonly citiesTypedToken!: ExcursionSearchCity & { label: string };
  readonly typesTypedToken!: ExcursionSearchType & { label: string };

  readonly formGroup = new FormGroup({
    countryCode: new FormControl<string | null>(null, [Validators.required]),
    date: new FormControl<Date>(new Date(this.today.getTime() + 24 * 3600 * 1000), [
      Validators.required,
      minDate(this.today, 'The excursion day must be today or later')
    ]),
    adults: new FormControl<number>(2),
    children: new FormControl<number>(0),
    childrenAges: new FormArray<FormControl<number | null>>([]),
    kinds: new FormControl<string[]>([]),
    // 25.09.2026: the filters the core search takes (@Cities, @ExcursionTypes, @PriceFrom / @PriceTo); the choices come from the prices on sale
    cityUIDs: new FormControl<string[]>([]),
    excursionTypeUIDs: new FormControl<string[]>([]),
    priceFrom: new FormControl<number | null>(null),
    priceTo: new FormControl<number | null>(null)
  });

  get countries() {
    return this.#selectors.destinationCountries;
  }
  get searchOptions() {
    return this.#selectors.searchOptions;
  }
  /** the Excursion module's own lists (pickup cities of the country, every category), by name as the module holds them */
  readonly cities = computed<(ExcursionSearchCity & { label: string })[]>(() =>
    (this.#selectors.searchOptions().response?.cities ?? []).map((c) => ({ ...c, label: c.cityName ?? '' }))
  );
  readonly types = computed<(ExcursionSearchType & { label: string })[]>(() =>
    (this.#selectors.searchOptions().response?.types ?? []).map((t) => ({ ...t, label: t.name ?? '' }))
  );
  get formStatus() {
    return this.#selectors.formStatus;
  }
  get kindsFilter() {
    return this.#selectors.kindsFilter;
  }
  get formValues() {
    return {
      countryCode: () => this.formGroup.controls.countryCode.value,
      date: () => this.formGroup.controls.date.value,
      adults: () => this.formGroup.controls.adults.value,
      children: () => this.formGroup.controls.children.value
    };
  }
  get hasChildrenAgesError() {
    return this.formGroup.controls.childrenAges.controls.some((c) => c.value === null || c.value === undefined);
  }

  /**
   * website-web#157 (R-EUI4): the country the ticked cities / categories belong to. The shared selector writes `countryCode` BEFORE it emits
   * `singleSelect`, so the control can never tell "another country" - this field can.
   */
  #optionsCountry: string | null = null;

  /** website-web#162 (R-EUI8): set when a refused key (minus, plus, exponent) was pressed in Price; the message says why the key did nothing */
  readonly priceKeyRefused = signal(false);

  init() {
    this.actions.getDestinationCountries();
    const stored = this.#selectors.activedDestinationCountry();
    if (stored && !this.formGroup.controls.countryCode.value) this.formGroup.controls.countryCode.setValue(stored);
    this.#optionsCountry = this.formGroup.controls.countryCode.value;
    if (this.formGroup.controls.countryCode.value)
      this.actions.getSearchOptions(this.formGroup.controls.countryCode.value);
  }

  /** C17: the country chooser's "Try again" action - re-dispatches the destination countries load with `refresh`. */
  retryDestinationCountries() {
    this.actions.getDestinationCountries(true);
  }

  onCountryChange(country: Country | null) {
    if (!country?.countryCode) return;
    const changed = country.countryCode !== this.#optionsCountry;
    this.#optionsCountry = country.countryCode;
    this.formGroup.controls.countryCode.setValue(country.countryCode);
    // R-EUI2 / R-EUI22 (docs#453 point 4): the chosen country is the remembered one - F5, a second tab or Back opens on it
    this.actions.rememberCountry(country.countryCode);
    // another country = other cities and categories; what was ticked for the old one is dropped
    if (changed) this.formGroup.patchValue({ cityUIDs: [], excursionTypeUIDs: [] });
    this.actions.getSearchOptions(country.countryCode);
  }

  onAddChildren(count: number) {
    // R-BIH42 (website-web#84): never below 0 - removeAt on an empty array does nothing, so a negative count looped forever and hung the tab
    const children = Math.max(count ?? 0, 0);
    const ages = this.formGroup.controls.childrenAges;
    while (ages.length > children) ages.removeAt(ages.length - 1);
    while (ages.length < children) ages.push(new FormControl<number | null>(null, [Validators.required]));
  }

  /** the Availability dropdown (Hotels-tab pattern): the kinds ticked narrow the results on the page; none ticked = every kind */
  onKindsChange(kinds: string[] | null) {
    this.actions.setKindsFilter(kinds ?? []);
  }

  /** R-EUI8: the Price inputs refuse a sign or an exponent (kzInput); the refusal is said under the field, and cleared by the next key */
  onPriceKey(event: KeyboardEvent) {
    this.priceKeyRefused.set(['-', '+', 'e', 'E'].includes(event.key));
  }

  onKindToggle(kind: string, checked: boolean) {
    const current = this.#selectors.kindsFilter() ?? [];
    this.actions.setKindsFilter(
      checked ? [...current.filter((k) => k !== kind), kind] : current.filter((k) => k !== kind)
    );
  }

  /** The request as Online.Api takes it (the agency comes from the login; the currency from the site's selector). */
  toRequest(): SearchOnlyExcursionPackagesRequest {
    const v = this.formGroup.getRawValue();
    const date = v.date ? new Date(v.date) : this.today;
    return deepClean({
      currency: this.#selectors.activedCurrency() ?? undefined,
      countryCode: v.countryCode ?? undefined,
      date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
      adults: v.adults ?? 1,
      children: v.childrenAges.length,
      childrenAges: v.childrenAges.filter((a): a is number => a !== null),
      cityUIDs: v.cityUIDs?.length ? v.cityUIDs : undefined,
      excursionTypeUIDs: v.excursionTypeUIDs?.length ? v.excursionTypeUIDs : undefined,
      priceFrom: v.priceFrom ?? undefined,
      priceTo: v.priceTo ?? undefined,
      live: true
    });
  }

  onSubmit() {
    // R-EUI10 (website-web#158): a second Search while the first runs is ignored - nothing written, so the drawn rows keep their criteria
    if (this.formStatus().status === 'loading') return;
    this.isSubmited.set(true);
    // R-EUI8 (website-web#162): a negative bound (pasted / dropped - the keys are refused) is held at 0, in the box and in the request. Done
    // before the validity check: the inputs' `min="0"` adds Angular's min validator, which otherwise blocked Search with no message
    for (const bound of [this.formGroup.controls.priceFrom, this.formGroup.controls.priceTo])
      if (bound.value !== null && bound.value < 0) bound.setValue(0);
    if (this.formGroup.invalid || this.hasChildrenAgesError) {
      this.formGroup.markAllAsTouched();
      return;
    }
    // R-EUI9 (website-web#159): the search clears the kinds filter in the state, so the Availability box is cleared with it
    this.formGroup.controls.kinds.setValue([]);
    // R-EUI2 / R-EUI22 (docs#453 point 4): the searched country is the remembered one
    const searched = this.formGroup.controls.countryCode.value;
    if (searched) this.actions.rememberCountry(searched);
    this.actions.updateFormValue({ path: this.ngxsForm, value: this.toRequest() });
    this.actions.searchPackages();
  }
}
